import {
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  CodeBlock,
  Editor,
  Essentials,
  FileRepository,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  HorizontalLine,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  Plugin,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
} from 'ckeditor5';
import { QualioCodeBlockPlugin } from '../plugins/CodeBlock/QualioCodeBlockPlugin';
import { QualioDisableSectionHeadingPlugin } from '../plugins/LockSectionHeaders/QualioDisableSectionHeadingPlugin';
import { QualioSectionHeadingPlugin } from '../plugins/SectionHeaders/QualioSectionHeadingPlugin';
import { CKEditorConfig } from './CKEditorConfig';
import { CONTENT_EDITOR_TOOLBAR_ITEMS } from './ToolbarItems';

declare global {
  const CKEDITOR: any;
  const Checkbox: any;
  const Smartlink: any;
  const SectionList: any;
  const FileUpload: any;
}

export class EditorConfig {
  public static CONTENT_EDITOR_PLUGINS: (typeof Plugin)[] = [
    Alignment,
    Autoformat,
    AutoImage,
    AutoLink,
    Autosave,
    BlockQuote,
    HorizontalLine,
    Essentials,
    Paragraph,
    Bold,
    FontColor,
    FontSize,
    FontFamily,
    FontBackgroundColor,
    Image,
    ImageInsert,
    ImageUpload,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    PageBreak,
    ImageToolbar,
    LinkImage,
    MediaEmbed,
    Indent,
    IndentBlock,
    RemoveFormat,
    Italic,
    Link,
    List,
    ListProperties,
    QualioSectionHeadingPlugin,
    PasteFromOffice,
    Mention,
    QualioDisableSectionHeadingPlugin,
    QualioCodeBlockPlugin,
    FileRepository,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    SpecialCharactersEssentials,
    Strikethrough,
    Superscript,
    Subscript,
    Heading,
    CodeBlock,
    Underline,
    Table,
    TableColumnResize,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    TableCaption,
    TextTransformation,
  ];

  private static EXTERNAL_PLUGINS_ADDED = false;

  private static addExternalPlugins() {
    if (EditorConfig.EXTERNAL_PLUGINS_ADDED) {
      return;
    }

    if (FileUpload.QualioFileUpload) {
      EditorConfig.CONTENT_EDITOR_PLUGINS.push(FileUpload.QualioFileUpload);
    }
    if (SectionList.QualioSectionList) {
      EditorConfig.CONTENT_EDITOR_PLUGINS.push(SectionList.QualioSectionList);
    }
    if (Checkbox.QualioCheckbox) {
      EditorConfig.CONTENT_EDITOR_PLUGINS.push(Checkbox.QualioCheckbox);
    }
    if (Smartlink.QualioSmartlink) {
      EditorConfig.CONTENT_EDITOR_PLUGINS.push(Smartlink.QualioSmartlink);
    }
  }

  public static getContentEditorPlugins() {
    EditorConfig.addExternalPlugins();
    return EditorConfig.CONTENT_EDITOR_PLUGINS;
  }

  public static getMentionConfig(companyId: number) {
    if (Smartlink.QualioSmartlink) {
      return Smartlink.QualioSmartlink.getMentionConfig(companyId, process.env.REACT_APP_GLOBAL_SEARCH_SERVICE_URL);
    }
  }

  public static readonly TABLE_TOOLBAR_ITEMS: string[] = [
    'tableColumn',
    'tableRow',
    'mergeTableCells',
    'toggleTableCaption',
    'tableProperties',
    'tableCellProperties',
  ];

  public static readonly IMAGE_TOOLBAR_ITEMS: string[] = [
    'toggleImageCaption',
    'imageTextAlternative',
    '|',
    'imageStyle:alignLeft',
    'imageStyle:alignRight',
    'imageStyle:alignBlockLeft',
    'imageStyle:alignBlockRight',
    'imageStyle:alignCenter',
  ];

  public static readonly EditorSmartlinkSuggestTimeout = 100;
}

export const getCKEditorConfig = (
  companyId: number,
  toolbarItems: string[] = CONTENT_EDITOR_TOOLBAR_ITEMS,
  handleAutoSave?: (editor: Editor) => Promise<void>,
): CKEditorConfig => {
  const config: CKEditorConfig = {
    companyId: companyId,
    plugins: [...EditorConfig.getContentEditorPlugins()],
    toolbar: {
      items: toolbarItems,
      shouldNotGroupWhenFull: false,
    },
    licenseKey: process.env.REACT_APP_CKE_PREMIUM_FEATURES_LICENSE_KEY,
    indentBlock: {
      offset: 2,
      unit: 'em',
    },
    table: {
      contentToolbar: EditorConfig.TABLE_TOOLBAR_ITEMS,
    },
    image: {
      toolbar: EditorConfig.IMAGE_TOOLBAR_ITEMS,
    },
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_p',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_h1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_h2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_h3',
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: 'ck-heading_h4',
        },
      ],
    },
    list: {
      properties: {
        styles: true,
        startIndex: false,
        reversed: false,
      },
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
    },
    mention: EditorConfig.getMentionConfig(companyId),
    fontFamily: {
      options: ['default'],
    },
    fontSize: {
      options: [
        'small',
        {
          title: 'Normal',
          model: '14px',
          upcastAlso: [],
          view: '',
        },
        {
          title: 'Large',
          model: '19.6px',
          upcastAlso: [],
          view: '',
        },
      ],
    },
    simpleFileUpload: {
      url: process.env.REACT_APP_MEDTECH_BACKEND_API_URL + '/attachments',
      fileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.png'],
    },
    simpleUpload: {
      uploadUrl: process.env.REACT_APP_MEDTECH_BACKEND_API_URL + '/attachments',
      withCredentials: true,
    },
  };

  if (handleAutoSave) {
    config.autosave = {
      save: handleAutoSave,
      waitingTime: 1000,
    };
  }

  return config;
};

const balloonPanelStyle = `
  .ck-balloon-panel_visible.ck-balloon-panel_with-arrow.ck-toolbar-container {
    z-index: 998 !important;
  }
`;

const documentLayoutStyle = `
  .gray-layout {
    padding: 0px !important;
  } 
`;

export const balloonPanelStyleSheet = document.createElement('style');
balloonPanelStyleSheet.innerText = balloonPanelStyle;

export const documentLayoutInnerStyleSheet = document.createElement('style');
documentLayoutInnerStyleSheet.innerText = documentLayoutStyle;
